"use client";

import { useTrans } from "@/translations";

import React from "react";

import { round } from "lodash";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { LabelContentType } from "recharts/types/component/Label";

import "./chart.css";

export type Translation = {
  key: string;
  ordinalString?: string;
  customerName?: string;
  numberOfTies?: number;
};

export type GraphData = {
  title: Translation;
  value: number | string;
  color: string;
};

type Props = {
  data: GraphData[];
  average: number;
};

const SortingComparisonChart: React.FC<Props> = ({ data, average }) => {
  return (
    <ResponsiveContainer width="100%" height={70 * data.length}>
      <BarChart data={data} layout="vertical" barCategoryGap={20}>
        <XAxis hide axisLine={false} type="number" />

        <Bar dataKey="value" background>
          {data.map((d, index) => (
            <Cell key={index} fill={d.color} />
          ))}
          <LabelList dataKey="title" content={CustomTitleLabelContent} />
          <LabelList
            dataKey="value"
            content={CustomValueLabelContent}
            fill="black"
            fontWeight={600}
            offset={20}
          />
        </Bar>

        <YAxis
          hide
          orientation="right"
          yAxisId={0}
          dataKey="value"
          type="category"
          axisLine={false}
          tickLine={false}
        />

        <ReferenceLine
          stroke="#FF8736"
          strokeDasharray="5 5"
          strokeWidth={2}
          x={average}
          label={(props) => (
            <CustomReferenceLineLabel {...props} average={average} />
          )}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const CustomReferenceLineLabel = (props: any) => {
  const { t } = useTrans();

  return (
    <foreignObject
      className="text-carrot bg-[#FFF3EB] text-center"
      style={{ backgroundColor: "#FFF3EB", color: "#FF8736" }}
      x={props.viewBox.x - 56} // substract half of the container width
      y={-55}
      width="112px" // Needed for PDF export
      height="56px" // Needed for PDF export
    >
      <div
        className="fixed bg-[#FFF3EB] p-2 text-center" // For Safari: setting fixed + repeating styles from foreignObject
        style={{
          paddingTop: "0.5rem", // Needed for PDF export
        }}
      >
        <div
          style={{
            fontWeight: 600, // Needed for PDF export
          }}
        >
          {round(props.average, 1)}%
        </div>
        <div
          style={{
            fontSize: "0.75rem", // Needed for PDF export
          }}
        >
          {t("circularityLabels:comparisonAverageTenant")}
        </div>
      </div>
    </foreignObject>
  );
};

const CustomValueLabelContent: LabelContentType = (props) => {
  const y = Number(props.y) + Number(props.height) / 1.55; // magic number for aligning text in the middle of the bar
  let x = Number(props.width) + 10;

  const value = Number(props.value);

  if (value > 10) {
    if (value.toString().length > 2) {
      x -= 60;
    } else {
      x -= 50;
    }
  }
  return (
    <text {...props.viewBox} y={y} x={x} fontWeight={400}>
      {value}%
    </text>
  );
};

const CustomTitleLabelContent: LabelContentType = (props) => {
  const { t } = useTrans();

  const { key, numberOfTies, ordinalString, customerName } =
    props.value as any as Translation;
  const y = Number(props.y);

  return (
    <text {...props.viewBox} y={y - 10} x={props.x} fontSize="14">
      {t(key, { numberOfTies, ordinalString, customerName })}
    </text>
  );
};

export default SortingComparisonChart;
